import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { ROUTES, getProjectPath, getWorkspacePath } from '@/constants/routes';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAccessRevocationStore } from '@/stores/accessRevocationStore';
import { useOrgContextStore } from '@/stores/orgContextStore';

const COUNTDOWN_SECONDS = 5;

interface AccessRevocationDialogContentProps {
  title: string;
  description: string;
  countdownHint: string;
  confirmLabel: string;
  onConfirm: () => void;
}

function AccessRevocationDialogContent({
  title,
  description,
  countdownHint,
  confirmLabel,
  onConfirm,
}: AccessRevocationDialogContentProps) {
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setSecondsLeft((current) => {
        if (current <= 1) {
          window.clearInterval(timer);
          onConfirm();
          return 0;
        }
        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [onConfirm]);

  return (
    <DialogContent
      hideCloseButton
      onEscapeKeyDown={(event) => event.preventDefault()}
      onPointerDownOutside={(event) => event.preventDefault()}
      className="w-80"
    >
      <DialogHeader>
        <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-error/10 text-error">
          <AlertTriangle className="h-6 w-6" aria-hidden />
        </div>
        <DialogTitle className="text-center">{title || '访问权限已变更'}</DialogTitle>
        <DialogDescription className="text-center">{description}</DialogDescription>
      </DialogHeader>
      <div className="rounded-xl bg-fill-lighter/60 px-4 py-3 text-center text-sm text-text-secondary">
        <span className="font-semibold text-text-primary">{secondsLeft}</span> {countdownHint}
      </div>
      <DialogFooter>
        <Button className="w-full" onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

export function AccessRevocationCountdownDialog() {
  const navigate = useNavigate();
  const dialog = useAccessRevocationStore((state) => state.dialog);
  const clear = useAccessRevocationStore((state) => state.clear);
  const activeOrgId = useOrgContextStore((state) => state.activeOrgId);
  const setActiveOrg = useOrgContextStore((state) => state.setActiveOrg);

  const handleConfirm = useCallback(() => {
    if (dialog.returnPath?.startsWith('/')) {
      clear();
      navigate(dialog.returnPath, { replace: true });
      return;
    }

    if (dialog.redirectTarget === 'project' && dialog.returnProjectId) {
      clear();
      navigate(getProjectPath(dialog.returnProjectId), { replace: true });
      return;
    }

    if (dialog.kind === 'org') {
      setActiveOrg(null, 0, { broadcast: false });
      clear();
      navigate(ROUTES.WORKSPACE, { replace: true });
      return;
    }

    clear();
    navigate(getWorkspacePath(activeOrgId), { replace: true });
  }, [
    activeOrgId,
    clear,
    dialog.kind,
    dialog.redirectTarget,
    dialog.returnPath,
    dialog.returnProjectId,
    navigate,
    setActiveOrg,
  ]);

  const countdownHint =
    dialog.redirectTarget === 'project' ? '秒后自动返回项目制作页' : '秒后自动返回工作台';
  const confirmLabel =
    dialog.redirectTarget === 'project' ? '立即返回项目制作页' : '立即返回工作台';

  return (
    <Dialog open={dialog.open} onOpenChange={() => undefined}>
      {dialog.open ? (
        <AccessRevocationDialogContent
          title={dialog.title}
          description={dialog.description}
          countdownHint={countdownHint}
          confirmLabel={confirmLabel}
          onConfirm={handleConfirm}
        />
      ) : null}
    </Dialog>
  );
}
